import { useQueryClient } from '@tanstack/react-query';
import { Flame, Info, RefreshCw, Trash2, TriangleAlert } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, useBlocker, useLocation, useSearchParams } from 'react-router';
import { isApiError } from '../api/client';
import { useApi } from '../api/context';
import {
  queryKeys,
  useAllCloudAccounts,
  useNetworkDiagnostics,
  useNetworkKinds,
  useNetworkRequests,
  useProviders,
} from '../api/queries';
import type {
  AttachPolicy,
  CrossRegion,
  Diagnostic,
  JsonObject,
  Network,
  NetworkValidation,
  PatchNetworkRequest,
  ProviderNetworkSettings,
  SpokeToSpoke,
  TopologyKind,
} from '../api/types';
import { usePermission } from '../auth/context';
import { useToast } from '../components/toast';
import { ActionButton, Button, Dialog, Field, FieldMessages } from '../components/ui';
import { useOptionalDock } from '../dock/context';
import { COMPACT_QUERY, useMediaQuery } from '../shell/breakpoints';
import { createMergePatch } from '../lib/mergePatch';
import { diffLeaves, jsonEqual, mergeThreeWay } from '../lib/settingsMerge';
import { strings } from '../strings';
import { NetworkDiagnostic } from './lists/parts';
import { useAllAttachments } from './page/hooks';
import { FirewallSection } from './settings/FirewallSection';
import {
  AWS_CENTRAL_EGRESS_MAX_HUB_PREFIX,
  firewallFromCost,
  offeredFirewalls,
  type FirewallForm,
} from './settings/helpers';
import { ImpactPanel } from './settings/ImpactPanel';
import { OutboundInternet } from './settings/OutboundInternet';
import { ShieldRenewalLine } from './settings/ShieldRenewalLine';
import { TopologyCards, TopologyOptions } from './settings/TopologyCards';
import { providerLabel, sizeOptionLabel, useManageNetworks, useNetworking } from './shared';
import type { NetworkSettingsTabProps } from './tabProps';

const l = strings.networks.lists;
const st = l.settings;
const c = l.create;

const NETWORK_SETTINGS_SECTIONS = [
  'general',
  'providers',
  'address-plan',
  'topology',
  'firewall',
  'dns',
  'danger',
] as const;
type Section = (typeof NETWORK_SETTINGS_SECTIONS)[number];
const t = strings.networks.topology;
const g = strings.networks.gcp;

interface ProviderForm {
  use: boolean;
  account: string;
  regions: string[];
  allowed: string[];
  /** Feature spec 17, phase 2: null while the settings have none and nobody turned it on. */
  firewall: FirewallForm | null;
  /** Feature spec 17, phase 4: AWS outbound internet without central egress (null: the default, NAT where needed). */
  egress: ProviderNetworkSettings['egress'];
}

interface SettingsForm {
  name: string;
  description: string;
  attachPolicy: AttachPolicy;
  topology: TopologyKind;
  spokeToSpoke: SpokeToSpoke | null;
  crossRegion: CrossRegion;
  /** Providers whose firewall the "Through the hub firewall" choice turned on (and Blocked turns off again). */
  firewallByChoice: string[];
  internalZone: string;
  privateLinkZones: 'auto' | 'all';
  providers: Record<string, ProviderForm>;
}

/** The parts of a network the tab edits, as one JSON document (diffs, merge patches, three-way merges). */
function docOf(n: Network): JsonObject {
  return { name: n.name, settings: n.settings, cloudAccounts: n.cloudAccounts } as unknown as JsonObject;
}

function toForm(n: Network, providers: readonly string[]): SettingsForm {
  const keys = [...new Set([...providers, ...Object.keys(n.settings.providers)])];
  return {
    name: n.name,
    description: n.settings.description ?? '',
    attachPolicy: n.settings.attachPolicy,
    topology: n.settings.topology.kind,
    spokeToSpoke: n.settings.topology.spokeToSpoke ?? null,
    crossRegion: n.settings.topology.crossRegion,
    firewallByChoice: [],
    internalZone: n.settings.dns.internalZone ?? '',
    privateLinkZones: n.settings.dns.privateLinkZones,
    providers: Object.fromEntries(
      keys.map((p) => {
        const settings = n.settings.providers[p as keyof typeof n.settings.providers];
        return [
          p,
          {
            use: !!settings,
            account: n.cloudAccounts[p] ?? '',
            regions: settings?.regions ?? [],
            allowed: settings?.allowedAccounts ?? [],
            firewall: settings?.firewall ?? null,
            egress: settings?.egress ?? null,
          },
        ];
      }),
    ),
  };
}

/** The form over the base network's document: members the tab doesn't edit stay as they are. */
function formDoc(form: SettingsForm, base: Network): JsonObject {
  const providers: Record<string, unknown> = {};
  const cloudAccounts: Record<string, string> = {};
  for (const [p, f] of Object.entries(form.providers)) {
    if (!f.use) continue;
    const before = base.settings.providers[p as keyof typeof base.settings.providers];
    if (p === 'cloudflare') {
      // Feature spec 17, phase 8 (spec 18 5.1): Cloudflare has only a connectivity account; no regions, allowed accounts,
      // firewall or egress.
      providers[p] = { regions: [] };
      if (f.account) cloudAccounts[p] = f.account;
      continue;
    }
    providers[p] = {
      ...before,
      regions: f.regions,
      allowedAccounts: [...new Set([f.account, ...f.allowed].filter(Boolean))],
      ...(f.firewall || (before && 'firewall' in before) ? { firewall: f.firewall } : {}),
      ...(f.egress || (before && 'egress' in before) ? { egress: f.egress } : {}),
    };
    if (f.account) cloudAccounts[p] = f.account;
  }
  const description = form.description.trim();
  const spokeToSpoke = form.topology === 'hub-spoke' ? form.spokeToSpoke : null;
  const { description: before, ...rest } = base.settings;
  // A cleared description is removed (null); an empty one stays as it was.
  const descriptionMember =
    description !== (before ?? '')
      ? { description: description || null }
      : before === undefined
        ? {}
        : { description: before };
  return {
    name: form.name.trim(),
    settings: {
      ...rest,
      ...descriptionMember,
      attachPolicy: form.attachPolicy,
      topology: {
        ...base.settings.topology,
        kind: form.topology,
        ...(spokeToSpoke !== null || 'spokeToSpoke' in base.settings.topology ? { spokeToSpoke } : {}),
        crossRegion: form.crossRegion,
      },
      providers,
      dns: {
        ...base.settings.dns,
        internalZone: form.internalZone.trim() || null,
        privateLinkZones: form.privateLinkZones,
      },
    },
    cloudAccounts,
  } as unknown as JsonObject;
}

function firewallOn(form: SettingsForm, provider: string): boolean {
  return !!form.providers[provider]?.firewall?.enabled;
}

function sectionOf(path: string): Section {
  if (path === '/name' || /^\/settings\/(description|attachPolicy)/.test(path)) return 'general';
  if (/^\/settings\/providers\/[^/]+\/firewall/.test(path)) return 'firewall';
  if (path.startsWith('/settings/providers') || path.startsWith('/cloudAccounts')) return 'providers';
  if (path.startsWith('/settings/addressPlan')) return 'address-plan';
  if (path.startsWith('/settings/topology')) return 'topology';
  if (path.startsWith('/settings/dns')) return 'dns';
  return 'general';
}

const MAX_RESENDS = 2;

/** The Settings tab (spec 11 6.15.12): sections, the dry run's impact line and the sticky save bar. */
export function NetworkSettingsTab({
  network,
  onDestroy,
  onDelete,
  destroyDisabledReason,
  deleteDisabledReason,
}: NetworkSettingsTabProps) {
  const api = useApi();
  const toast = useToast();
  const dock = useOptionalDock();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [, setSearch] = useSearchParams();
  const ids = useId();
  const manage = useManageNetworks();
  const operate = usePermission('operate');
  const networking = useNetworking();
  const providersQuery = useProviders();
  const accounts = useAllCloudAccounts(manage || operate);
  const kinds = useNetworkKinds(network.data.id);
  const attachments = useAllAttachments(network.data.id);
  const compact = useMediaQuery(COMPACT_QUERY);
  const impactRef = useRef<HTMLElement>(null);
  const [base, setBase] = useState(network);
  const [seen, setSeen] = useState(network);
  const [form, setForm] = useState<SettingsForm>(() => toForm(network.data, networking.providers));
  const requests = useNetworkRequests(
    network.data.id,
    {},
    { enabled: form.topology === 'micro-segmentation' },
  );
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveDiagnostics, setSaveDiagnostics] = useState<Diagnostic[]>([]);
  const [current, setCurrent] = useState<Section | null>(null);

  const baseDoc = docOf(base.data);
  const mine = formDoc(form, base.data);
  const changes = manage ? diffLeaves(baseDoc, mine) : [];
  const changed = new Set(changes.map((x) => sectionOf(x.path)));
  const patch = createMergePatch(baseDoc, mine) as PatchNetworkRequest;
  const patchKey = JSON.stringify(patch);
  const update = (next: Partial<SettingsForm>) => {
    setForm((f) => ({ ...f, ...next }));
    setSaveDiagnostics([]);
  };
  const updateProvider = (provider: string, next: Partial<ProviderForm>) =>
    setForm((f) => ({
      ...f,
      providers: { ...f.providers, [provider]: { ...f.providers[provider], ...next } },
    }));

  // Rebase (6.13 item 9): the network changed while the tab is open (a poll, someone else's save).
  if (network !== seen) {
    setSeen(network);
    const theirs = docOf(network.data);
    if (!jsonEqual(theirs, baseDoc)) {
      if (changes.length === 0) {
        setBase(network);
        setForm(toForm(network.data, networking.providers));
      } else {
        const merged = mergeThreeWay<JsonObject>(baseDoc, mine, theirs);
        const next = { ...network.data, ...(merged.merged as unknown as Pick<Network, 'name' | 'settings'>) };
        setBase(network);
        setForm(toForm(next as Network, networking.providers));
        setMessage(
          merged.conflicts.length ? st.conflict(merged.conflicts.map((x) => x.path).join(', ')) : st.rebased,
        );
      }
    } else if (network.etag !== base.etag) setBase(network);
  }

  // Leaving with unsaved changes asks first (6.13 item 10); the page's own tabs don't.
  const unsaved = changes.length > 0;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => unsaved && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (!unsaved) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [unsaved]);

  // The anchors (`#providers`, …) scroll to their section once.
  useEffect(() => {
    const key = location.hash.slice(1);
    if ((NETWORK_SETTINGS_SECTIONS as readonly string[]).includes(key))
      document.getElementById(`${ids}-${key}`)?.scrollIntoView?.({ block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The dry run (debounced 400 ms; the last answer wins): diagnostics at the fields and the save bar's line.
  const [dry, setDry] = useState<{
    key: string;
    state: 'loading' | 'done' | 'error';
    data: NetworkValidation | null;
  } | null>(null);
  const [retry, setRetry] = useState(0);
  const latest = useRef(0);
  useEffect(() => {
    if (!unsaved) return;
    const request = ++latest.current;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setDry((d) => ({ key: patchKey, state: 'loading', data: d?.data ?? null }));
      api
        .validateNetwork(base.data.id, JSON.parse(patchKey) as PatchNetworkRequest, controller.signal)
        .then((data) => {
          if (request === latest.current) setDry({ key: patchKey, state: 'done', data });
        })
        .catch(() => {
          if (request === latest.current && !controller.signal.aborted)
            setDry((d) => ({ key: patchKey, state: 'error', data: d?.data ?? null }));
        });
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [api, base.data.id, patchKey, unsaved, retry]);

  const dryData = unsaved && dry?.key === patchKey ? dry.data : unsaved ? (dry?.data ?? null) : null;
  // Feature spec 17, phase 6: the saved network's GCP_DEPLOYING_IDENTITY_UNKNOWN shows under its project without a change.
  const saved = useNetworkDiagnostics(base.data.id, manage && !!base.data.settings.providers.gcp && !unsaved);
  const diagnostics = [
    ...saveDiagnostics,
    ...(unsaved && dry?.state === 'done' ? (dryData?.diagnostics ?? []) : []),
    ...(!unsaved ? (saved.data ?? []).filter((d) => d.code === 'GCP_DEPLOYING_IDENTITY_UNKNOWN') : []),
  ];
  const errors = diagnostics.filter((d) => d.severity === 'error');
  const at = (prefix: string) =>
    diagnostics.filter((d) => (d.path ?? '') === prefix || (d.path ?? '').startsWith(`${prefix}/`));
  const messagesAt = (prefix: string) =>
    at(prefix)
      .filter((d) => d.severity === 'error')
      .map((d) => d.message);

  // In-use facts (changes the server refuses because of attachments are disabled before Save, 6.15.12).
  const live = (attachments.data ?? []).filter((a) => a.state !== 'detached');
  const inProvider = (p: string) => live.filter((a) => a.provider === p).length;
  const inRegion = (p: string, r: string) => live.filter((a) => a.provider === p && a.region === r).length;
  const regionsOf = (p: string) => providersQuery.data?.find((x) => x.key === p)?.regions ?? [];
  const accountsOf = (p: string) => (accounts.data ?? []).filter((a) => a.provider === p);
  const accountName = (id: string) => accounts.data?.find((a) => a.id === id)?.name ?? c.accountById(id);
  const providerKeys = Object.keys(form.providers);

  const choiceMissing =
    manage &&
    form.topology === 'hub-spoke' &&
    !!form.providers.azure?.use &&
    !firewallOn(form, 'azure') &&
    !form.spokeToSpoke;

  const discard = () => {
    setForm(toForm(base.data, networking.providers));
    setMessage(null);
    setSaveDiagnostics([]);
  };

  const save = async () => {
    if (errors.length || choiceMissing) return;
    let target = base;
    let body = patch;
    if (Object.keys(body).length === 0) return;
    setBusy(true);
    setMessage(null);
    try {
      for (let attempt = 0; ; attempt++) {
        try {
          const saved = await api.patchNetwork(target.data.id, body, target.etag);
          queryClient.setQueryData(queryKeys.network(saved.data.id), saved);
          void queryClient.invalidateQueries({ queryKey: ['networks'] });
          void queryClient.invalidateQueries({ queryKey: ['network', saved.data.id] });
          setBase(saved);
          setSeen(saved);
          setForm(toForm(saved.data, networking.providers));
          setDry(null);
          const text = st.saved(saved.data.name);
          dock?.announce(text);
          toast.show({ tone: 'success', title: text });
          return;
        } catch (error) {
          if (isApiError(error, 'PRECONDITION_FAILED') && attempt < MAX_RESENDS) {
            const fresh = await api.getNetwork(target.data.id);
            queryClient.setQueryData(queryKeys.network(fresh.data.id), fresh);
            setSeen(fresh);
            if (jsonEqual(docOf(fresh.data), docOf(target.data))) {
              target = fresh;
              setBase(fresh);
              body = createMergePatch(docOf(fresh.data), mine) as PatchNetworkRequest;
              continue;
            }
            const merged = mergeThreeWay<JsonObject>(docOf(target.data), mine, docOf(fresh.data));
            const next = {
              ...fresh.data,
              ...(merged.merged as unknown as Pick<Network, 'name' | 'settings'>),
            };
            setBase(fresh);
            setForm(toForm(next as Network, networking.providers));
            setMessage(
              merged.conflicts.length
                ? st.conflict(merged.conflicts.map((x) => x.path).join(', '))
                : st.rebased,
            );
            return;
          }
          if (isApiError(error) && error.diagnostics.length > 0) setSaveDiagnostics(error.diagnostics);
          else if (isApiError(error) && error.code !== 'PRECONDITION_FAILED')
            setSaveDiagnostics([
              { severity: 'error', code: error.code, message: error.detail ?? error.message, path: '' },
            ]);
          else toast.error(error);
          return;
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const recreated = dryData?.impact?.addresses.changed
    ? dryData.impact.addresses.recreatedSpokes.map((x) => x.name).join(', ')
    : '';
  const impactLine: ReactNode =
    dry?.state === 'error' ? (
      <>
        {st.impactUnknown}{' '}
        <button type="button" className="link" onClick={() => setRetry((n) => n + 1)}>
          <RefreshCw aria-hidden className="mr-1 inline size-3.5" />
          {strings.common.retry}
        </button>
      </>
    ) : dryData?.impact ? (
      <span className={dry?.state === 'loading' ? 'italic' : undefined}>
        {dryData.impact.summary}
        {recreated && ` · ${st.recreated(recreated)}`}
        {dry?.state === 'loading' && ` · ${st.updating}`}
      </span>
    ) : dry?.state === 'loading' || !dry ? (
      <span className="italic">{st.updating}</span>
    ) : null;

  const card = (key: Section, children: ReactNode) => (
    <section
      key={key}
      id={`${ids}-${key}`}
      aria-labelledby={`${ids}-${key}-title`}
      className="settings-card"
      data-settings-section={key}
    >
      <div className="settings-card-head">
        <h2 id={`${ids}-${key}-title`} className="text-[15px] font-semibold">
          {st.sections[key]}
        </h2>
        {changed.has(key) && <span className="badge tone-info">{st.changed}</span>}
      </div>
      <div className="settings-card-body">{children}</div>
    </section>
  );

  const n = base.data;
  const plan = n.settings.addressPlan;
  const topologies = kinds.data?.topologies ?? [];
  // Feature spec 17, phase 2: topology options, the firewall and the impact panel.
  const usedProviders = providerKeys.filter((p) => form.providers[p].use);
  const firewallEntries = offeredFirewalls(
    kinds.data?.firewalls ?? [],
    usedProviders,
    usedProviders.filter((p) => !!form.providers[p].firewall),
  );
  const sections = NETWORK_SETTINGS_SECTIONS.filter(
    (key) => key !== 'firewall' || firewallEntries.length > 0,
  );
  // Cloud NGFW (phase 6) filters egress at each VPC, so it doesn't make hub topologies cheaper.
  const anyFirewall = usedProviders.some((p) => p !== 'gcp' && firewallOn(form, p));
  const fromCost = firewallFromCost(kinds.data?.firewalls ?? [], usedProviders);
  const blockers = choiceMissing ? [t.chooseSpokes] : [];
  const showImpact = manage && (changed.has('topology') || changed.has('firewall'));
  const approvedRequests = (requests.data ?? [])
    .flatMap((g) => g.instances)
    .filter((i) => i.applied?.decision === 'approved' && !i.withdrawnAt).length;
  const baseFirewall = (p: string) => toForm(base.data, networking.providers).providers[p]?.firewall ?? null;
  const setFirewall = (p: string, firewall: FirewallForm | null) => {
    setSaveDiagnostics([]);
    setForm((f) => ({
      ...f,
      firewallByChoice: firewall?.enabled ? f.firewallByChoice : f.firewallByChoice.filter((x) => x !== p),
      providers: { ...f.providers, [p]: { ...f.providers[p], firewall } },
    }));
  };
  const chooseTopology = (kind: TopologyKind) => {
    setSaveDiagnostics([]);
    setForm((f) => {
      const back = kind === 'hub-spoke' ? [] : f.firewallByChoice;
      const providers = { ...f.providers };
      for (const p of back) providers[p] = { ...providers[p], firewall: baseFirewall(p) };
      const applied = base.data.settings.topology;
      const byChoice = f.firewallByChoice.filter((p) => !back.includes(p));
      // AWS (phase 4, user decision 2026-11-02): hub-spoke turns AWS Network Firewall and central egress on in the form,
      // visibly with both prices and the saving; the settings' default stays off.
      const aws = providers.aws;
      const awsEntry = kinds.data?.firewalls.find((x) => x.provider === 'aws' && x.status === 'available');
      if (kind === 'hub-spoke' && aws?.use && awsEntry && !aws.firewall?.enabled) {
        const central = n.settings.addressPlan.hubPrefixLength <= AWS_CENTRAL_EGRESS_MAX_HUB_PREFIX;
        providers.aws = {
          ...aws,
          firewall: {
            allowedEgress: [],
            ...aws.firewall,
            enabled: true,
            tier: aws.firewall?.tier ?? awsEntry.tiers[0] ?? 'network-firewall',
            egress: central ? 'through-firewall' : 'direct',
          },
        };
        byChoice.push('aws');
      }
      // Google Cloud (phase 6, user decision 2026-11-02 as on AWS): hub-spoke turns Cloud NGFW Standard on in the form,
      // with egress through it (on Google Cloud it filters only egress) and its cost lines; the settings' default stays off.
      const gcp = providers.gcp;
      const gcpEntry = kinds.data?.firewalls.find((x) => x.provider === 'gcp' && x.status === 'available');
      if (kind === 'hub-spoke' && gcp?.use && gcpEntry && !gcp.firewall?.enabled) {
        providers.gcp = {
          ...gcp,
          firewall: {
            allowedEgress: [],
            ...gcp.firewall,
            enabled: true,
            tier: 'standard',
            egress: 'through-firewall',
          },
        };
        byChoice.push('gcp');
      }
      // On a network whose only provider is AWS, via-hub costs nothing extra: preselected instead of asked.
      const awsOnly = !!aws?.use && !Object.entries(providers).some(([p, x]) => p !== 'aws' && x.use);
      return {
        ...f,
        topology: kind,
        // Never preselected on Azure without the firewall (a cost decision); kept when it is the saved choice. Google
        // Cloud supports only blocked (GCP_HUB_TRANSIT_NOT_SUPPORTED).
        spokeToSpoke:
          kind === 'hub-spoke' && gcp?.use
            ? 'blocked'
            : kind === 'hub-spoke' && applied.kind === 'hub-spoke'
              ? (applied.spokeToSpoke ?? (awsOnly ? 'via-hub' : null))
              : kind === 'hub-spoke' && awsOnly
                ? 'via-hub'
                : null,
        firewallByChoice: byChoice,
        providers,
      };
    });
  };
  const chooseSpokes = (value: SpokeToSpoke) => {
    setSaveDiagnostics([]);
    setForm((f) => {
      const azure = f.providers.azure;
      if (value === 'via-hub' && azure && !azure.firewall?.enabled) {
        // The paid firewall is turned on only through this choice, visibly in the Firewall section.
        const entry = kinds.data?.firewalls.find((x) => x.provider === 'azure');
        const tier = entry?.tiers.includes('standard') ? 'standard' : (entry?.tiers[0] ?? 'standard');
        const firewall: FirewallForm = {
          egress: 'direct',
          allowedEgress: [],
          ...azure.firewall,
          enabled: true,
          tier: azure.firewall?.tier ?? tier,
        };
        return {
          ...f,
          spokeToSpoke: value,
          firewallByChoice: [...f.firewallByChoice, 'azure'],
          providers: { ...f.providers, azure: { ...azure, firewall } },
        };
      }
      if (value === 'blocked' && f.firewallByChoice.includes('azure') && azure)
        return {
          ...f,
          spokeToSpoke: value,
          firewallByChoice: f.firewallByChoice.filter((p) => p !== 'azure'),
          providers: { ...f.providers, azure: { ...azure, firewall: baseFirewall('azure') } },
        };
      return { ...f, spokeToSpoke: value };
    });
  };
  const viewRequests = (a: string, b: string) =>
    setSearch(new URLSearchParams({ tab: 'traffic', pair: `${a}~${b}` }), { replace: true });
  const showImpactPanel = () => {
    impactRef.current?.scrollIntoView?.({ block: 'start' });
    impactRef.current?.focus();
  };
  const impactState: 'loading' | 'done' | 'error' | 'idle' = !dry ? 'loading' : dry.state;
  const topologyMessages = at('/settings/topology').filter(
    (d) => d.code !== 'TOPOLOGY_CHOICE_REQUIRED' || !choiceMissing,
  );

  return (
    <div className="settings-layout" data-testid="network-settings">
      <nav aria-label={st.nav} className="settings-nav">
        <ul>
          {sections.map((key) => (
            <li key={key}>
              <button
                type="button"
                className={`settings-nav-item ${current === key ? 'is-current' : ''}`}
                aria-label={changed.has(key) ? st.sectionChanged(st.sections[key]) : undefined}
                onClick={() => {
                  setCurrent(key);
                  document.getElementById(`${ids}-${key}`)?.scrollIntoView?.({ block: 'start' });
                }}
              >
                {st.sections[key]}
                {changed.has(key) && <span aria-hidden className="settings-dot" />}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <fieldset className="settings-sections" disabled={busy} aria-busy={busy || undefined}>
        {!manage && <p className="callout callout-info">{st.readOnly}</p>}
        {message && (
          <p role="status" className="callout callout-warning" data-testid="settings-conflict">
            <TriangleAlert aria-hidden className="size-4 shrink-0" />
            {message}
          </p>
        )}
        {saveDiagnostics.filter((d) => !d.path).length > 0 && (
          <div role="alert" className="callout callout-danger">
            <ul>
              {saveDiagnostics
                .filter((d) => !d.path)
                .map((d, i) => (
                  <li key={i}>
                    <NetworkDiagnostic diagnostic={d} />
                  </li>
                ))}
            </ul>
          </div>
        )}

        {card(
          'general',
          manage ? (
            <div className="grid max-w-2xl gap-3">
              <Field label={st.name} htmlFor={`${ids}-name`} errors={messagesAt('/name')} required>
                <input
                  id={`${ids}-name`}
                  className="input max-w-sm"
                  value={form.name}
                  maxLength={64}
                  aria-invalid={messagesAt('/name').length ? true : undefined}
                  onChange={(e) => update({ name: e.target.value })}
                />
              </Field>
              <Field
                label={st.description}
                htmlFor={`${ids}-description`}
                errors={messagesAt('/settings/description')}
              >
                <input
                  id={`${ids}-description`}
                  className="input"
                  value={form.description}
                  onChange={(e) => update({ description: e.target.value })}
                />
              </Field>
              <fieldset>
                <legend className="label">{c.whoMayAttach}</legend>
                {(
                  [
                    ['operators', c.operators, c.operatorsHelp],
                    ['network-admins', c.networkAdmins, c.networkAdminsHelp],
                  ] as const
                ).map(([value, label, help]) => (
                  <label key={value} className="mb-1 flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name={`${ids}-policy`}
                      className="mt-1"
                      checked={form.attachPolicy === value}
                      onChange={() => update({ attachPolicy: value })}
                    />
                    <span>
                      <span className="font-medium">{label}</span>
                      <span className="block text-fg-muted">{help}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
            </div>
          ) : (
            <dl className="settings-dl">
              <dt>{st.name}</dt>
              <dd>{n.name}</dd>
              <dt>{st.description}</dt>
              <dd>{n.settings.description || st.none}</dd>
              <dt>{c.whoMayAttach}</dt>
              <dd>{n.settings.attachPolicy === 'operators' ? c.operators : c.networkAdmins}</dd>
            </dl>
          ),
        )}

        {card(
          'providers',
          <div className="space-y-4">
            {providerKeys.map((provider) => {
              const p = form.providers[provider];
              const name = providerLabel(provider);
              const gateOn = networking.providers.includes(provider);
              const editable = manage && gateOn;
              const attached = inProvider(provider);
              const accountReason =
                attached > 0 && p.use && n.cloudAccounts[provider] ? st.connectivityInUse(attached) : null;
              const accountOptions = accountsOf(provider);
              return (
                <fieldset key={provider} className="net-provider-block" data-testid={`provider-${provider}`}>
                  <legend className="sr-only">{name}</legend>
                  {editable ? (
                    <label className="net-switch-label flex items-center gap-2 font-semibold">
                      <input
                        type="checkbox"
                        role="switch"
                        checked={p.use}
                        aria-disabled={p.use && attached > 0 ? true : undefined}
                        aria-describedby={p.use && attached > 0 ? `${ids}-${provider}-inuse` : undefined}
                        onChange={(e) => {
                          if (p.use && attached > 0) return;
                          updateProvider(provider, { use: e.target.checked });
                        }}
                      />
                      {st.useProvider(name)}
                    </label>
                  ) : (
                    <p className="font-semibold">
                      {name}
                      {!p.use && <span className="ml-2 font-normal text-fg-muted">{st.notUsed}</span>}
                    </p>
                  )}
                  {p.use && attached > 0 && (
                    <span id={`${ids}-${provider}-inuse`} className="sr-only">
                      {st.connectivityInUse(attached)}
                    </span>
                  )}
                  {!gateOn && <p className="help mt-1">{st.switchedOff}</p>}
                  {p.use && (
                    <div className="mt-2 grid gap-x-4 gap-y-2 sm:grid-cols-2">
                      <Field
                        label={provider === 'gcp' ? g.connectivityProject : c.connectivityAccount}
                        htmlFor={`${ids}-account-${provider}`}
                        help={
                          accountReason ??
                          (provider === 'gcp'
                            ? g.connectivityProjectHelp
                            : provider === 'cloudflare'
                              ? c.connectivityHelpCloudflare
                              : c.connectivityHelp)
                        }
                        errors={messagesAt(`/cloudAccounts/${provider}`)}
                      >
                        {editable && accounts.data && !accountReason ? (
                          <select
                            id={`${ids}-account-${provider}`}
                            className="input"
                            value={p.account}
                            onChange={(e) => updateProvider(provider, { account: e.target.value })}
                          >
                            <option value="">{c.chooseAccount}</option>
                            {accountOptions.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            id={`${ids}-account-${provider}`}
                            className="input"
                            readOnly
                            aria-disabled={editable ? true : undefined}
                            value={p.account ? accountName(p.account) : ''}
                          />
                        )}
                      </Field>
                      {provider !== 'cloudflare' && (
                        <>
                          <fieldset>
                            <legend className="label">{st.regions}</legend>
                            <ul className="net-region-list">
                              {(editable
                                ? [...new Set([...regionsOf(provider).map((r) => r.name), ...p.regions])]
                                : p.regions
                              ).map((region) => {
                                const inUse = inRegion(provider, region);
                                const on = p.regions.includes(region);
                                const reason = on && inUse > 0 ? st.regionInUse(inUse, region) : null;
                                const reasonId = `${ids}-${provider}-${region}-reason`;
                                return (
                                  <li key={region} className="flex flex-wrap items-center gap-2 text-sm">
                                    {editable ? (
                                      <label className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={on}
                                          aria-disabled={reason ? true : undefined}
                                          aria-describedby={reason ? reasonId : undefined}
                                          onChange={(e) => {
                                            if (reason) return;
                                            updateProvider(provider, {
                                              regions: e.target.checked
                                                ? [...p.regions, region]
                                                : p.regions.filter((x) => x !== region),
                                            });
                                          }}
                                        />
                                        <span className="font-mono">{region}</span>
                                      </label>
                                    ) : (
                                      <span className="font-mono">{region}</span>
                                    )}
                                    {reason && (
                                      <span id={reasonId} className="text-[12px] text-fg-muted">
                                        {reason}
                                      </span>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                            <FieldMessages messages={messagesAt(`/settings/providers/${provider}/regions`)} />
                          </fieldset>
                          <fieldset className="sm:col-span-2">
                            <legend className="label">
                              {provider === 'gcp' ? g.allowedProjects : st.allowedAccounts}
                            </legend>
                            <ul className="space-y-1">
                              {(editable && accounts.data
                                ? accountOptions.map((a) => a.id)
                                : [...new Set([p.account, ...p.allowed].filter(Boolean))]
                              ).map((id) => {
                                const connectivity = id === p.account;
                                const helpId = `${ids}-${provider}-allowed-${id}`;
                                // Phase 4: AWS_SHARE_NEEDS_ACCEPTANCE and AWS_ORGANIZATION_NOT_CHECKED under the account.
                                const index = [...new Set([p.account, ...p.allowed].filter(Boolean))].indexOf(
                                  id,
                                );
                                const accountNotes =
                                  index < 0
                                    ? []
                                    : at(`/settings/providers/${provider}/allowedAccounts/${index}`);
                                return (
                                  <li key={id}>
                                    {editable ? (
                                      <label className="flex items-center gap-2 text-sm">
                                        <input
                                          type="checkbox"
                                          checked={connectivity || p.allowed.includes(id)}
                                          aria-disabled={connectivity ? true : undefined}
                                          aria-describedby={connectivity ? helpId : undefined}
                                          onChange={(e) => {
                                            if (connectivity) return;
                                            updateProvider(provider, {
                                              allowed: e.target.checked
                                                ? [...p.allowed, id]
                                                : p.allowed.filter((x) => x !== id),
                                            });
                                          }}
                                        />
                                        {accountName(id)}
                                      </label>
                                    ) : (
                                      <span className="text-sm">{accountName(id)}</span>
                                    )}
                                    {connectivity && editable && (
                                      <p id={helpId} className="help ml-6">
                                        {provider === 'gcp'
                                          ? g.connectivityAlwaysAllowed
                                          : c.connectivityAlwaysAllowed}
                                      </p>
                                    )}
                                    {accountNotes.map((d, i) =>
                                      d.code === 'AWS_SHARE_NEEDS_ACCEPTANCE' ? (
                                        <p key={i} className="help ml-6" data-testid="aws-share-acceptance">
                                          {t.awsShareNeedsAcceptance}
                                        </p>
                                      ) : d.code === 'GCP_DEPLOYING_IDENTITY_UNKNOWN' ? (
                                        // Phase 6: the deploying identity of an allowed project is unknown until it is
                                        // verified; Verify opens the cloud account.
                                        <p key={i} className="ml-6" data-testid="gcp-identity-unknown">
                                          <NetworkDiagnostic diagnostic={d} />{' '}
                                          <Link
                                            className="link"
                                            to={`/cloud-accounts/${id}`}
                                            aria-label={g.verifyProject(accountName(id))}
                                          >
                                            {g.verify}
                                          </Link>
                                        </p>
                                      ) : (
                                        <p key={i} className="ml-6">
                                          <NetworkDiagnostic diagnostic={d} />
                                        </p>
                                      ),
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                            <ul className="mt-1 space-y-1">
                              {at(`/settings/providers/${provider}/allowedAccounts`)
                                .filter(
                                  (d) =>
                                    !/\/allowedAccounts\/\d+$/.test(d.path) ||
                                    !(d.code.startsWith('AWS_') || d.code.startsWith('GCP_')),
                                )
                                .map((d, i) => (
                                  <li key={i}>
                                    <NetworkDiagnostic diagnostic={d} />
                                  </li>
                                ))}
                            </ul>
                          </fieldset>
                        </>
                      )}
                      {provider === 'aws' &&
                        !(p.firewall?.enabled && p.firewall.egress === 'through-firewall') && (
                          <OutboundInternet
                            value={p.egress ?? null}
                            readOnly={!editable}
                            onChange={(egress) => updateProvider(provider, { egress })}
                          />
                        )}
                      {provider === 'aws' && <ShieldRenewalLine network={network.data} />}
                    </div>
                  )}
                </fieldset>
              );
            })}
            {at('/settings/providers')
              .filter((d) => d.path === '/settings/providers')
              .map((d, i) => (
                <NetworkDiagnostic key={i} diagnostic={d} />
              ))}
          </div>,
        )}

        {card(
          'address-plan',
          <>
            <dl className="settings-dl">
              <dt>{st.supernets}</dt>
              <dd className="font-mono">{plan.supernets.join(', ') || st.none}</dd>
              <dt>{st.reserved}</dt>
              <dd>
                {plan.reserved.length
                  ? plan.reserved.map((r) => `${r.cidr} (${r.label})`).join(', ')
                  : st.none}
              </dd>
              <dt>{st.lengths}</dt>
              <dd>{st.lengthsValue(plan.regionPrefixLength, plan.hubPrefixLength)}</dd>
              <dt>{st.defaultSize}</dt>
              <dd>{sizeOptionLabel(plan.defaultAttachmentSize)}</dd>
            </dl>
            <button
              type="button"
              className="btn btn-sm mt-3"
              onClick={() => setSearch(new URLSearchParams({ tab: 'address-plan' }), { replace: true })}
            >
              {st.editInTab}
            </button>
          </>,
        )}

        <div className={showImpact ? 'net-topology-area has-impact' : 'net-topology-area'}>
          <div className="net-topology-cards">
            {card(
              'topology',
              topologies.length ? (
                <>
                  <TopologyCards
                    topologies={topologies}
                    firewalls={kinds.data?.firewalls ?? []}
                    providers={usedProviders}
                    value={form.topology}
                    applied={
                      n.lastSuccessfulApply && !n.settingsChangedSinceApply ? n.settings.topology.kind : null
                    }
                    firewallOn={anyFirewall}
                    readOnly={!manage}
                    network={n.name}
                    awsCosts={networking.providers.includes('aws')}
                    gcpCosts={networking.providers.includes('gcp')}
                    onChange={chooseTopology}
                  />
                  <TopologyOptions
                    kind={form.topology}
                    spokeToSpoke={form.spokeToSpoke}
                    crossRegion={form.crossRegion}
                    firewallOn={firewallOn(form, 'azure')}
                    firewallCost={fromCost}
                    firewallRegions={(form.providers.azure?.regions ?? []).join(', ')}
                    approved={approvedRequests}
                    pending={n.requests.pending}
                    readOnly={!manage}
                    choiceMissing={choiceMissing}
                    awsOnly={usedProviders.length === 1 && usedProviders[0] === 'aws'}
                    gcp={usedProviders.includes('gcp')}
                    onSpokeToSpoke={(value) => {
                      if (!(value === 'via-hub' && usedProviders.includes('gcp'))) chooseSpokes(value);
                    }}
                    onCrossRegion={(value) => update({ crossRegion: value })}
                    messages={
                      topologyMessages.length > 0 ? (
                        <ul className="space-y-1">
                          {topologyMessages.map((d, i) => (
                            <li key={i}>
                              <NetworkDiagnostic diagnostic={d} />
                            </li>
                          ))}
                        </ul>
                      ) : null
                    }
                  />
                </>
              ) : (
                <p className="help">{strings.common.loading}</p>
              ),
            )}
            {sections.includes('firewall') &&
              card(
                'firewall',
                <FirewallSection
                  entries={firewallEntries}
                  value={Object.fromEntries(usedProviders.map((p) => [p, form.providers[p].firewall]))}
                  readOnly={!manage}
                  turnedOnByChoice={form.firewallByChoice}
                  microSegmentation={form.topology === 'micro-segmentation'}
                  hubPrefixLength={plan.hubPrefixLength}
                  regionCount={Object.fromEntries(
                    usedProviders.map((p) => [p, form.providers[p].regions.length]),
                  )}
                  diagnosticsAt={at}
                  onChange={setFirewall}
                />,
              )}
          </div>
          {showImpact && (
            <ImpactPanel
              impact={dryData?.impact ?? null}
              state={impactState}
              panelRef={impactRef}
              onRetry={() => setRetry((x) => x + 1)}
              onViewRequests={viewRequests}
            />
          )}
        </div>

        {card(
          'dns',
          manage ? (
            <div className="grid max-w-2xl gap-3">
              <Field
                label={st.internalZone}
                htmlFor={`${ids}-zone`}
                help={st.internalZoneHelp}
                errors={messagesAt('/settings/dns/internalZone')}
              >
                <input
                  id={`${ids}-zone`}
                  className="input max-w-sm font-mono"
                  value={form.internalZone}
                  onChange={(e) => update({ internalZone: e.target.value })}
                />
              </Field>
              <fieldset>
                <legend className="label">{st.privateLinkZones}</legend>
                {(
                  [
                    ['auto', st.auto],
                    ['all', st.all],
                  ] as const
                ).map(([value, label]) => (
                  <label key={value} className="mb-1 flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name={`${ids}-zones`}
                      checked={form.privateLinkZones === value}
                      onChange={() => update({ privateLinkZones: value })}
                    />
                    {label}
                  </label>
                ))}
              </fieldset>
            </div>
          ) : (
            <dl className="settings-dl">
              <dt>{st.internalZone}</dt>
              <dd className="font-mono">{n.settings.dns.internalZone || st.none}</dd>
              <dt>{st.privateLinkZones}</dt>
              <dd>{n.settings.dns.privateLinkZones === 'all' ? st.all : st.auto}</dd>
            </dl>
          ),
        )}

        {card(
          'danger',
          <>
            <p className="help mb-3">{st.destroyHelp}</p>
            <div className="flex flex-wrap gap-2">
              <ActionButton
                permission="manage-networks"
                variant="danger"
                disabledReason={destroyDisabledReason}
                icon={<Flame aria-hidden className="size-4" />}
                onClick={onDestroy}
              >
                {st.destroy}
              </ActionButton>
              <ActionButton
                permission="manage-networks"
                disabledReason={deleteDisabledReason}
                icon={<Trash2 aria-hidden className="size-4" />}
                onClick={onDelete}
              >
                {st.delete}
              </ActionButton>
            </div>
          </>,
        )}
      </fieldset>

      {manage && unsaved && (
        <div
          role="region"
          aria-label={st.saveBar}
          className={`save-bar ${showImpact ? 'has-impact' : ''}`}
          data-testid="save-bar"
        >
          <Info aria-hidden className="size-4 shrink-0 text-accent-fg" />
          <p className="min-w-0 flex-1 text-sm" aria-live={showImpact ? undefined : 'polite'}>
            <span className="font-semibold">{st.changes(changes.length)}</span>
            {impactLine && <> · {impactLine}</>}
            {errors.length > 0 && <span className="block text-danger">{st.errorSummary(errors.length)}</span>}
            {blockers.map((b) => (
              <span key={b} className="block font-semibold text-warning" data-testid="save-blocker">
                {b}
              </span>
            ))}
          </p>
          {showImpact && compact && <Button onClick={showImpactPanel}>{t.viewImpact}</Button>}
          <Button onClick={discard} disabled={busy}>
            {st.discard}
          </Button>
          <Button
            variant="primary"
            onClick={() => void save()}
            disabled={busy}
            aria-disabled={errors.length > 0 || blockers.length > 0 ? true : undefined}
          >
            {busy ? st.saving : st.save}
          </Button>
        </div>
      )}
      {blocker.state === 'blocked' && (
        <Dialog
          title={st.leaveTitle}
          onClose={() => blocker.reset()}
          footer={
            <>
              <Button onClick={() => blocker.reset()}>{st.stay}</Button>
              <Button variant="danger" onClick={() => blocker.proceed()}>
                {st.leave}
              </Button>
            </>
          }
        >
          <p data-autofocus tabIndex={-1}>
            {st.leaveMessage}
          </p>
        </Dialog>
      )}
    </div>
  );
}
